// Окна подтверждения с неотправленными правками (015: сценарий 4.4, 4.5, сценарий 5; FR-021,
// FR-023; plan.md, «Ответы на чек-лист ux», CHK005, CHK006):
// - отказ: «Отменить несохранённые правки?» — «Оставить правки» / «Отменить правки» (опасная);
// - уход из протокола внутри приложения: «Правки ещё не сохранены» — «Остаться» / «Уйти»
//   (правки остаются в браузере и уйдут при следующем открытии протокола);
// - выход из системы: «Выйти и потерять правки?» — «Остаться» / «Выйти» (опасная).
// Текст называет последствие числом правок. Безопасная кнопка — в фокусе при открытии, действие —
// последним справа (ux.md, «Окно подтверждения»).
//
// Отступление от «Табеля» (SHOULD, «Окно подтверждения»): ConfirmDialog из @kp/ui подписывает
// отмену только «Отмена», а ux CHK005 и CHK006 требуют «Оставить правки» и «Остаться». Окна
// собраны на Modal: у них есть ✕ и щелчок мимо закрывает — оба значат безопасный ответ
// («Оставить правки», «Остаться»); иконки в цветном квадрате нет. Строка — в «Отложено» плана.
import { Button, Modal, ModalFooter } from '@kp/ui';
import { useState } from 'react';
import type { RefObject } from 'react';
import { discardText, leaveText, logoutText } from './texts.ts';
import './status.css';

export interface LeaveDialogProps {
  open: boolean;
  /** false — безопасный ответ: кнопка отмены, ✕, Esc, щелчок мимо; и после действия. */
  onOpenChange: (open: boolean) => void;
  /** Неотправленные правки: очередь и команда в полёте. */
  count: number;
  /**
   * Действие окна. Промис — спиннер на кнопке, окно закрывается после него; отклонённый промис
   * оставляет окно открытым (сообщение показывает вызывающий). Отказ ждёт ответа на команду
   * в полёте (сценарий 5.3).
   */
  onConfirm: () => void | Promise<unknown>;
  /** Куда вернуть фокус после закрытия, если окно открыли из меню. */
  returnFocusRef?: RefObject<HTMLElement | null>;
}

interface PendingConfirmProps extends LeaveDialogProps {
  title: string;
  text: string;
  stayLabel: string;
  confirmLabel: string;
  danger: boolean;
}

function PendingConfirm({
  open,
  onOpenChange,
  onConfirm,
  returnFocusRef,
  title,
  text,
  stayLabel,
  confirmLabel,
  danger,
}: PendingConfirmProps) {
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch {
      // Сообщение об ошибке — у вызывающего; окно остаётся, правки целы.
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      open={open}
      // Пока действие идёт, окно не закрывается: итог отказа ещё не известен.
      onOpenChange={(next) => (busy ? undefined : onOpenChange(next))}
      size="sm"
      className="kp-leave"
      title={title}
      // Последствие — в шапке, как у ConfirmDialog: диктор читает его вместе с заголовком.
      description={text}
      {...(returnFocusRef ? { returnFocusRef } : {})}
      footer={
        <ModalFooter
          cancelLabel={null}
          primary={
            <>
              <Button
                variant="secondary"
                data-autofocus
                disabled={busy}
                onClick={() => onOpenChange(false)}
              >
                {stayLabel}
              </Button>
              <Button
                variant={danger ? 'danger' : 'primary'}
                loading={busy}
                onClick={() => void confirm()}
              >
                {confirmLabel}
              </Button>
            </>
          }
        />
      }
    />
  );
}

/**
 * Отказ от несохранённых правок (CHK005): после него таблица показывает версию сервера. count —
 * всё, что пропадёт: неотправленные правки, свой текст у ячеек и строки, которые сервер не создал
 * (Snapshot.discardCount, T045).
 */
export function DiscardDialog(props: LeaveDialogProps) {
  return (
    <PendingConfirm
      {...props}
      title="Отменить несохранённые правки?"
      text={discardText(props.count)}
      stayLabel="Оставить правки"
      confirmLabel="Отменить правки"
      danger
    />
  );
}

/** Уход из протокола в другой раздел (CHK006): правки остаются в этом браузере. */
export function LeaveProtocolDialog(props: LeaveDialogProps) {
  return (
    <PendingConfirm
      {...props}
      title="Правки ещё не сохранены"
      text={leaveText(props.count)}
      stayLabel="Остаться"
      confirmLabel="Уйти"
      danger={false}
    />
  );
}

/** Выход из системы (CHK006, FR-021): очередь пользователя очищается. */
export function LogoutDialog(props: LeaveDialogProps) {
  return (
    <PendingConfirm
      {...props}
      title="Выйти и потерять правки?"
      text={logoutText(props.count)}
      stayLabel="Остаться"
      confirmLabel="Выйти"
      danger
    />
  );
}
